import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Ticket, CalendarDays, CheckCircle2 } from 'lucide-react';

/**
 * Shown after a successful ticket claim/purchase (free, cash-reserved, or paid).
 * Replaces the previous boring toast so users know exactly where to find their
 * tickets — wallet for QR codes, calendar for the upcoming event date.
 */
const PurchaseConfirmDialog = ({ open, onOpenChange, eventName, ticketCount = 1, paymentType = 'free' }) => {
  const navigate = useNavigate();

  const heading = paymentType === 'cash'
    ? 'Tickets reserved!'
    : paymentType === 'paid'
      ? 'Tickets confirmed!'
      : 'Free tickets claimed!';

  const subheading = paymentType === 'cash'
    ? `${ticketCount} ticket${ticketCount > 1 ? 's' : ''} reserved — pay cash on the door at ${eventName}.`
    : `${ticketCount} ticket${ticketCount > 1 ? 's' : ''} for ${eventName} are now in your Ticket Wallet.`;

  const goWallet = () => { onOpenChange(false); navigate('/my-tickets'); };
  const goCalendar = () => { onOpenChange(false); navigate('/calendar'); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-md" data-testid="purchase-confirm-dialog">
        <DialogHeader className="space-y-3">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <DialogTitle className="text-center text-xl font-heading">{heading}</DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed">
            {subheading}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <Button
            onClick={goWallet}
            className="w-full rounded-full h-12 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
            data-testid="purchase-confirm-wallet-btn"
          >
            <Ticket className="w-4 h-4 mr-2" />
            View My Ticket Wallet
          </Button>
          <Button
            onClick={goCalendar}
            variant="outline"
            className="w-full rounded-full h-12 border-pink-500/40 hover:bg-pink-500/10 text-pink-400 font-semibold"
            data-testid="purchase-confirm-calendar-btn"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            View My Calendar
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            variant="ghost"
            className="w-full rounded-full text-xs text-muted-foreground"
            data-testid="purchase-confirm-dismiss-btn"
          >
            Stay here
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseConfirmDialog;
